import BackendService from 'BackendService';

export const contactCache = new Map<string, { name: string; loading: boolean }>();

export function isActivityLogContentDiffEvent(eventType: string): boolean {
  return (
    eventType === 'CONTENT_DRAFT_SAVED' ||
    eventType === 'CONTENT_PUBLISHED' ||
    eventType === 'CONTENT_AI_GENERATED' ||
    eventType === 'CONTENT_AI_REFINED' ||
    eventType === 'ARTICLE_AI_GENERATED' ||
    eventType === 'ARTICLE_AI_REFINED' ||
    eventType === 'ARTICLE_PUBLISHED'
  );
}

export function isActivityLogEmailEvent(eventType: string): boolean {
  return (
    eventType === 'EMAIL_SENT' ||
    eventType === 'EMAIL_SEND_FAILED' ||
    eventType === 'EMAIL_OPENED' ||
    eventType === 'EMAIL_CLICKED'
  );
}

export function isActivityLogEmailChangeEvent(eventType: string): boolean {
  return eventType === 'EMAIL_CHANGE_PENDING' || eventType === 'EMAIL_CHANGE_CONFIRMED';
}

export function isActivityLogPushNotificationEvent(eventType: string): boolean {
  return (
    eventType === 'PUSH_NOTIFICATION_SENT' ||
    eventType === 'PUSH_NOTIFICATION_DELIVERED' ||
    eventType === 'PUSH_NOTIFICATION_OPENED' ||
    eventType === 'PUSH_NOTIFICATION_DISMISSED'
  );
}

export function getActivityLogDisplayName(
  description: any,
  contactNames: Map<string, string>,
  modelType?: string,
  externalId?: string,
): string {
  if (!description) {
    if (modelType === 'CONTACT' && externalId) {
      const cached = contactCache.get(externalId);
      if (cached?.name) return cached.name;
      const stateCached = contactNames.get(externalId);
      if (stateCached) return stateCached;
    }
    return 'Unknown';
  }

  if (description.name) return description.name;
  if (description.contactName && description.fundOpportunityName) {
    return `${description.contactName} (${description.fundOpportunityName})`;
  }
  if (description.contactName) return description.contactName;
  if (description.fundOpportunityName) return description.fundOpportunityName;
  if (description.entityName) return description.entityName;

  if (modelType === 'CONTACT' && externalId) {
    const cached = contactCache.get(externalId);
    if (cached?.name) return cached.name;
    const stateCached = contactNames.get(externalId);
    if (stateCached) return stateCached;
  }

  return 'Unknown';
}

export async function fetchActivityLogContactIfNeeded(
  backendService: BackendService,
  modelType: string,
  externalId: string,
  onNameResolved: (id: string, name: string) => void,
): Promise<void> {
  if (modelType !== 'CONTACT' || !externalId) return;

  const cached = contactCache.get(externalId);
  if (cached?.name || cached?.loading) return;

  contactCache.set(externalId, { name: '', loading: true });

  try {
    const contact = await backendService.getContact(externalId);
    const name = `${contact.firstName} ${contact.lastName}`.trim();
    contactCache.set(externalId, { name, loading: false });
    onNameResolved(externalId, name);
  } catch (error) {
    console.error(`Failed to fetch contact ${externalId}:`, error);
    contactCache.set(externalId, { name: 'Unknown', loading: false });
  }
}

export function parseActivityLogExtendedInfo(
  extendedInfo: unknown,
  options: { isEmailChangeEvent: boolean; isUserProfileUpdated: boolean },
): {
  extendedInfo: any;
  hasEmailChangeInProfileUpdate: boolean;
} {
  let parsed = extendedInfo;
  let hasEmailChangeInProfileUpdate = false;

  if (options.isEmailChangeEvent && typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      console.error('Failed to parse extendedInfo:', e);
    }
  }

  if (options.isUserProfileUpdated && typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      console.error('Failed to parse extendedInfo:', e);
    }
  }

  if (
    options.isUserProfileUpdated &&
    (parsed as { email?: { from?: string; to?: string; status?: string } })?.email?.from &&
    (parsed as { email?: { from?: string; to?: string; status?: string } })?.email?.to &&
    (parsed as { email?: { from?: string; to?: string; status?: string } })?.email?.status ===
      'pending_confirmation'
  ) {
    hasEmailChangeInProfileUpdate = true;
  }

  return { extendedInfo: parsed, hasEmailChangeInProfileUpdate };
}
